/**
 * Values as the TZ-UI «Значення» rules write them: numbers in full with the
 * unit in the label, dates «19.10.2026» in both languages, times in the
 * object's timezone.
 */
import type { Locale } from '../i18n/messages.js';

const NUMBER_TAG: Record<Locale, string> = { uk: 'uk-UA', en: 'en-GB' };

export function fmtNum(locale: Locale, v: number, digits = 1): string {
  return new Intl.NumberFormat(NUMBER_TAG[locale], {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(v);
}

/** Up to `digits` decimals, trailing zeros dropped (capacity 32,3 · power 12). */
export function fmtNumShort(locale: Locale, v: number, digits = 1): string {
  return new Intl.NumberFormat(NUMBER_TAG[locale], { maximumFractionDigits: digits }).format(v);
}

export function fmtMoney(locale: Locale, v: number): string {
  return new Intl.NumberFormat(NUMBER_TAG[locale], { maximumFractionDigits: 0 }).format(Math.round(v));
}

/** `2026-10-19` → «19.10.2026». */
export function fmtDate(ymd: string): string {
  const [y, m, d] = ymd.split('-');
  return `${d}.${m}.${y}`;
}

/** `2026-10-19` → «19.10». */
export function fmtDayMonth(ymd: string): string {
  const [, m, d] = ymd.split('-');
  return `${d}.${m}`;
}

function parts(iso: string | Date, tz: string): Record<string, string> {
  const out: Record<string, string> = {};
  const f = new Intl.DateTimeFormat('en-GB', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
  for (const p of f.formatToParts(typeof iso === 'string' ? new Date(iso) : iso)) out[p.type] = p.value;
  return out;
}

/** Local calendar day of an instant: `YYYY-MM-DD`. */
export function localYmd(iso: string | Date, tz: string): string {
  const p = parts(iso, tz);
  return `${p.year}-${p.month}-${p.day}`;
}

/** Local wall-clock hour of an instant, 0–23. */
export function localHour(iso: string | Date, tz: string): number {
  return Number(parts(iso, tz).hour);
}

/** «14:35» in `tz`. */
export function fmtTime(iso: string | Date, tz: string): string {
  const p = parts(iso, tz);
  return `${p.hour}:${p.minute}`;
}

/** «20.10.2026, 14:35» in `tz`. */
export function fmtDateTime(iso: string | Date, tz: string): string {
  const p = parts(iso, tz);
  return `${p.day}.${p.month}.${p.year}, ${p.hour}:${p.minute}`;
}

/** «14:35», or «20.10.2026, 14:35» when the instant is not on `today`. */
export function fmtSince(iso: string, tz: string, today: string): string {
  return localYmd(iso, tz) === today ? fmtTime(iso, tz) : fmtDateTime(iso, tz);
}

/** `YYYY-MM-DD` shifted by whole days. */
export function addDays(ymd: string, days: number): string {
  const t = Date.parse(`${ymd}T12:00:00Z`) + days * 86_400_000;
  return new Date(t).toISOString().slice(0, 10);
}

/** Serial number of a Deye inverter: `vendor_site_id` = `${stationId}:${deviceSn}`. */
export function serialOf(vendorSiteId: string): string {
  const i = vendorSiteId.indexOf(':');
  return i >= 0 ? vendorSiteId.slice(i + 1) : vendorSiteId;
}

/** «Deye SUN-12K-SG04LP3». */
export function modelLabel(model: string | null): string | null {
  if (!model) return null;
  return /^deye\b/i.test(model) ? model : `Deye ${model}`;
}

/** Initials for the avatar: «Олена Коваль» → «ОК», `owner@example.com` → «OW». */
export function initials(nameOrEmail: string): string {
  const words =
    nameOrEmail
      .split('@')[0]
      ?.split(/[\s._-]+/)
      .filter(Boolean) ?? [];
  const two =
    words.length >= 2 ? `${words[0]?.[0] ?? ''}${words[1]?.[0] ?? ''}` : (words[0] ?? '').slice(0, 2);
  return two.toUpperCase();
}

/** A number typed with a comma or a dot; NaN when it is not one. */
export function parseNum(text: string): number {
  const t = text.replace(/[\s  ]/g, '').replace(',', '.');
  return t === '' || !/^-?\d*\.?\d+$/.test(t) ? Number.NaN : Number(t);
}
